// Statistics filter types
export interface StatisticsFilters {
  startDate?: string;
  endDate?: string;
  buildingId?: string;
  label?: string;
  granularity?: 'day' | 'week' | 'month';
}

// Dashboard stats
export interface DashboardStats {
  openTickets: number;
  newThisWeek: number;
  completedThisWeek: number;
  completedThisMonth: number;
  avgOpenDays: number;
  slaCompliance: number;
  backlogHealthScore: number;
  statusDistribution: StatusDistributionItem[];
  priorityDistribution: PriorityDistributionItem[];
}

export interface StatusDistributionItem {
  status: string;
  count: number;
  percentage: number;
}

export interface PriorityDistributionItem {
  priority: string;
  count: number;
  percentage: number;
}

// Throughput metrics (Doorlooptijd)
export interface ThroughputMetrics {
  avgDays: number;
  medianDays: number;
  withinSla: number;
  totalCompleted: number;
  slaComplianceRate: number;
  byPriority: PriorityThroughput[];
  trend: ThroughputTrendItem[];
}

export interface PriorityThroughput {
  priority: string;
  avgDays: number;
  medianDays: number;
  withinSla: number;
  total: number;
  slaTarget: number;
}

export interface ThroughputTrendItem {
  period: string;
  avgDays: number;
  completedCount: number;
}

// Phase duration analysis
export interface PhaseDuration {
  phase: string;
  phaseLabel: string;
  avgDays: number;
  medianDays: number;
  transitionCount: number;
  isBottleneck: boolean;
}

// Time series data
export interface TimeSeriesData {
  granularity: string;
  data: TimeSeriesDataPoint[];
  totals: {
    created: number;
    completed: number;
    netChange: number;
  };
}

export interface TimeSeriesDataPoint {
  period: string;
  periodLabel: string;
  created: number;
  completed: number;
  runningBacklog: number;
  createdTicketIds: number[];
  completedTicketIds: number[];
}

// Building backlog evolution - time series of open ticket snapshots per building
export interface BuildingBacklogEvolution {
  // Time series data: open ticket count per building at end of each period
  buildings: BuildingEvolutionData[];
  // Period labels (e.g., "W01", "W02", etc. or "Jan", "Feb", etc.)
  periods: string[];
  // Period keys for reference (e.g., "2025-01-06", "2025-01-13", etc.)
  periodKeys: string[];
  // Granularity used
  granularity: 'week' | 'month';
  // Buildings sorted by current open count (most tickets first)
  topBuildingsByVolume: BuildingRankingItem[];
  // Buildings with biggest increase (being left behind)
  risingBuildings: BuildingChangeItem[];
  // Buildings with biggest decrease (improving)
  decliningBuildings: BuildingChangeItem[];
  // Comparison period label
  comparisonLabel: string;
  // Ticket movements per period (for drill-down)
  periodMovements: PeriodTicketMovement[];
}

// Ticket movements for a specific period
export interface PeriodTicketMovement {
  periodKey: string;
  periodLabel: string;
  // Tickets created in this period
  created: TicketMovementItem[];
  // Tickets completed in this period
  completed: TicketMovementItem[];
}

export interface TicketMovementItem {
  id: number;
  title: string;
  buildingId: string;
  buildingName: string;
  label: string;
  date: string; // createdAt or completedAt/changedAt
}

export interface BuildingEvolutionData {
  buildingId: string;
  buildingName: string;
  // Array of open ticket counts, one per period (aligned with periods array)
  openCounts: number[];
  // Current open count (most recent period)
  currentOpenCount: number;
  // Change from previous period
  changeFromPrevious: number;
  // Change from comparison period (e.g., last month)
  changeFromComparison: number;
}

export interface BuildingRankingItem {
  buildingId: string;
  buildingName: string;
  currentOpenCount: number;
  rank: number;
}

export interface BuildingChangeItem {
  buildingId: string;
  buildingName: string;
  currentOpenCount: number;
  previousOpenCount: number;
  absoluteChange: number;
  percentageChange: number;
}

// Distribution metrics
export interface Distributions {
  byBuilding: BuildingDistributionItem[];
  byLabel: LabelDistributionItem[];
  bySource: SourceDistributionItem[];
}

export interface BuildingDistributionItem {
  buildingId: string;
  buildingName: string;
  count: number;
  percentage: number;
  avgDoorlooptijd: number;
}

export interface LabelDistributionItem {
  label: string;
  count: number;
  percentage: number;
  avgDoorlooptijd: number;
}

export interface SourceDistributionItem {
  source: string;
  count: number;
  percentage: number;
}

// Action items
export interface ActionItems {
  stuckTickets: StuckTicketItem[];
  pendingSupplier: PendingSupplierItem[];
  slaAtRisk: SlaAtRiskItem[];
  responseTimeAtRisk: ResponseTimeAtRiskItem[];
  counts: {
    stuck: number;
    pendingSupplier: number;
    slaAtRisk: number;
    responseTimeAtRisk: number;
  };
  dataSources: {
    slaAtRisk: string;
    responseTimeAtRisk: string;
    stuckTickets: string;
    pendingSupplier: string;
  };
}

export interface StuckTicketItem {
  id: number;
  title: string;
  daysSinceActivity: number;
  building: string;
  buildingId: string;
  label: string;
  internalStatus: string;
  source: string;
}

export interface PendingSupplierItem {
  id: number;
  title: string;
  daysInStatus: number;
  supplier: string;
  supplierId: number;
  building: string;
  label: string;
  source: string;
}

export interface SlaAtRiskItem {
  id: number;
  title: string;
  priority: string;
  daysOpen: number;
  slaTarget: number;
  daysRemaining: number;
  building: string;
  label: string;
  source: string;
}

// Response time at risk - tickets without first response within 72 hours
export interface ResponseTimeAtRiskItem {
  id: number;
  title: string;
  hoursWithoutResponse: number;
  currentPhase: string;
  building: string;
  label: string;
  source: string;
  createdAt: string;
}

// Response Time SLA constants
export const RESPONSE_TIME_SLA_HOURS = 72; // 3 days
export const RESPONSE_TIME_WARNING_HOURS = 48;

// Home dashboard summary
export interface HomeDashboardSummary {
  doorlooptijd: {
    avg: number;
    trend: 'up' | 'down' | 'stable';
    slaCompliance: number;
  };
  alerts: {
    stuck: number;
    pendingSupplier: number;
    slaAtRisk: number;
    highPriorityOpen: number;
  };
  volume: {
    openTickets: number;
    completedThisWeek: number;
    newThisWeek: number;
    netChange: number;
  };
  backlogHealth: number;
}

// Combined statistics data
export interface StatisticsData {
  dashboard: DashboardStats | null;
  throughput: ThroughputMetrics | null;
  phaseDurations: PhaseDuration[] | null;
  timeSeries: TimeSeriesData | null;
  distributions: Distributions | null;
  actionItems: ActionItems | null;
}

// Loading states
export interface StatisticsLoadingState {
  dashboard: boolean;
  throughput: boolean;
  phaseDurations: boolean;
  timeSeries: boolean;
  distributions: boolean;
  actionItems: boolean;
}

// SLA Configuration (mirrors backend)
export const SLA_TARGETS: Record<string, number> = {
  'Hoog': 5,
  'Gemiddeld': 10,
  'Laag': 20,
  'Unknown': 10,
};

export const SLA_WARNING_THRESHOLDS: Record<string, number> = {
  'Hoog': 3,
  'Gemiddeld': 7,
  'Laag': 14,
  'Unknown': 7,
};

// Extended Distribution Filters
export interface ExtendedDistributionFilters {
  ticketStatus?: 'open' | 'closed' | 'all';
  year?: string;
  month?: string;
}

// Extended Distribution Types
export interface ExtendedDistributions {
  byBuilding: ExtendedBuildingDistributionItem[];
  byLabel: ExtendedLabelDistributionItem[];
  bySource: SourceDistributionItem[];
  buildingLabelCombination: BuildingLabelCombinationItem[];
  analysis: AnalysisData;
  filters: {
    ticketStatus: string;
    year: string;
    month: string;
  };
  availableYears: number[];
}

export interface ExtendedBuildingDistributionItem {
  buildingId: string;
  buildingName: string;
  count: number;
  percentage: number;
  avgDoorlooptijd: number;
}

export interface ExtendedLabelDistributionItem {
  label: string;
  count: number;
  percentage: number;
  avgDoorlooptijd: number;
}

export interface BuildingLabelCombinationItem {
  buildingId: string;
  buildingName: string;
  label: string;
  count: number;
  percentage: number;
}

// Cluster-level analysis types
export interface ClusterAnalysisItem {
  clusterId: number;
  clusterName: string;
  totalTickets: number;
  categories: CategoryCount[];
  buildings: BuildingInCluster[];
}

export interface BuildingInCluster {
  buildingId: string;
  totalTickets: number;
  categories: CategoryCount[];
  units: UnitInBuilding[];
}

export interface UnitInBuilding {
  unitId: string;
  unitName: string;
  totalTickets: number;
  categories: CategoryCount[];
}

export interface CategoryCount {
  label: string;
  count: number;
}

export interface AnalysisData {
  clusters: ClusterAnalysisItem[];
  unclustered: BuildingInCluster[];
  allCategories: string[];
  totals: {
    byCategory: CategoryCount[];
    total: number;
  };
}

// ── Uitgaven (OPEX-facturen gekoppeld aan tickets) ──────────────────────────

export interface ExpenseFilters {
  /** Jaar van de FACTUURdatum, of 'all'. */
  year?: string;
  /** Maand van de factuurdatum ('01'..'12'), of 'all'. */
  month?: string;
}

export interface ExpenseItem {
  /** Gebouw-id of ticketlabel; 'Onbekend' als het ticket dat niet ingevuld heeft. */
  key: string;
  /** Som van de gekoppelde factuurregels, excl. btw. */
  amount: number;
  invoiceCount: number;
  lineCount: number;
  ticketCount: number;
  percentage: number;
}

export interface ExpenseDistributions {
  byBuilding: ExpenseItem[];
  byLabel: ExpenseItem[];
  total: number;
  invoiceCount: number;
  filters: { year: string; month: string };
  availableYears: number[];
}

// ── Top huurders (melders met de meeste tickets) ────────────────────────────

export interface TopTenantsFilters {
  year?: string;
  month?: string;
  limit?: number;
  /** Alleen contacten met het label Huurder/Ex-huurder. */
  onlyTaggedTenants?: boolean;
}

export interface TopTenantItem {
  contactId: number;
  name: string;
  email: string | null;
  tags: string[];
  isTaggedTenant: boolean;
  ticketCount: number;
  openTicketCount: number;
  buildingIds: string[];
  lastTicketAt: string | null;
}

export interface TopTenants {
  tenants: TopTenantItem[];
  totalTickets: number;
  filters: { year: string; month: string; onlyTaggedTenants: boolean };
  availableYears: number[];
}
